/**
 * Inscrição do tablet nas salas do servidor (BF3, DP-BF-007).
 *
 * Todo tablet autenticado entra na sala da turma, para receber o que é da turma
 * inteira (ex.: `boss_final_iniciada`), e mantém a sala do grupo, onde chegam os
 * eventos `ROOM_ONLY`. A sala é por conexão no servidor: a inscrição precisa se
 * repetir a cada `pronto`, inclusive depois de reconexão.
 */

/** Espera base entre tentativas de entrar na sala do grupo; cresce a cada recusa seguida. */
const ESPERA_BASE_MS = 1500;
/** Depois disso o tablet desiste até o próximo `pronto`: não insiste para sempre. */
const MAX_TENTATIVAS = 5;

/**
 * @param {{ inscreverNaSala: (sala: string) => any }} rede
 * @param {{ turmaId?: string|null, grupoId?: string|null }} sessao
 */
export function inscreverSalasDaSessao(rede, sessao) {
    if (sessao?.turmaId) rede.inscreverNaSala(`turma:${sessao.turmaId}`);
    if (sessao?.grupoId) rede.inscreverNaSala(`grupo:${sessao.grupoId}`);
}

/**
 * Liga a inscrição ao evento `pronto` da rede.
 *
 * `sala_lotada` não é definitivo: um socket morto do próprio tablet ocupa vaga até o
 * heartbeat do servidor (15 s). O tablet repete o pedido do grupo, com espera crescente,
 * em vez de ficar fora da sala só com um aviso no console.
 *
 * @param {object} rede
 * @param {{ agendar?: Function, cancelar?: Function }} [opcoes] agendador injetável (teste)
 * @returns {() => void} desliga os ouvintes
 */
export function ligarInscricaoDeSalas(rede, opcoes = {}) {
    const agendar = opcoes.agendar ?? ((fn, ms) => setTimeout(fn, ms));
    const cancelar = opcoes.cancelar ?? ((id) => clearTimeout(id));
    let sessaoAtual = null;
    let tentativas = 0;
    let temporizador = null;

    const cancelarPendente = () => {
        if (temporizador !== null) cancelar(temporizador);
        temporizador = null;
    };

    const desligarPronto = rede.on('pronto', (sessao) => {
        sessaoAtual = sessao;
        tentativas = 0;
        cancelarPendente();
        inscreverSalasDaSessao(rede, sessao);
    });

    const desligarInscrita = rede.on('sala_inscrita', (mensagem) => {
        // Só a confirmação do grupo encerra a retentativa; a da turma chega no mesmo `pronto`.
        if (String(mensagem?.room_id ?? '').startsWith('grupo:')) cancelarPendente();
    });

    const desligarRecusa = rede.on('sala_recusada', (mensagem) => {
        if (mensagem?.type !== 'sala_lotada') return;
        if (!sessaoAtual?.grupoId || tentativas >= MAX_TENTATIVAS) return;
        tentativas += 1;
        cancelarPendente();
        temporizador = agendar(() => {
            temporizador = null;
            rede.inscreverNaSala(`grupo:${sessaoAtual.grupoId}`);
        }, ESPERA_BASE_MS * tentativas);
    });

    return () => {
        cancelarPendente();
        desligarPronto?.();
        desligarInscrita?.();
        desligarRecusa?.();
    };
}
